/**
 * Token 存取。
 *
 * **access token 只在内存里；refresh token 落盘。** —— 这是 CR-006 的结果，
 * 部分推翻了 CR-003 当初「一律不落盘」的做法，读之前建议先看 docs/CHANGES.md。
 *
 * 为什么可以落盘了：会话的结束时机从「进程生命周期」换成了「下班打卡」。
 * 工人打完下班卡就清干净，管理员不打卡所以常驻。刷新页面、App 被系统回收
 * 都不再需要重新输密码。
 *
 * 为什么只落 refresh token：access token 两小时就过期，存它没意义；
 * refresh token 在服务端可吊销（管理员强制下班会立刻作废它），
 * 落盘的那一份随时能被远端废掉，比一个无法撤回的长效凭证安全。
 *
 * 仍然没有放开的：登录表单依旧 `autoComplete="off"`，不让 WebView 记住
 * 上一个人的账号密码。
 *
 * 单独一个模块也是为了打破循环依赖：api/client 要读 token，
 * store/auth 要调 api/client，两边都依赖这里就不会绕回去。
 */

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

/** 换过 key：老版本里存的是完整 token 对，语义不同，不能直接复用 */
const STORAGE_KEY = 'construction.session.v2';

let tokens: TokenPair | null = null;

export function getTokens(): TokenPair | null {
  return tokens;
}

export function setTokens(next: TokenPair): void {
  tokens = next;
  persist(next.refreshToken);
}

export function clearTokens(): void {
  tokens = null;
  persist(null);
}

/**
 * 冷启动时把盘上的 refresh token 装回内存。
 *
 * access token 留空：第一个请求会拿到 401，client 里的刷新逻辑会用
 * refresh token 换一套新的再重试。不用为「启动」单独写一条换 token 的路径。
 */
export function restorePersistedSession(): boolean {
  const refreshToken = read();
  if (!refreshToken) return false;
  tokens = { accessToken: '', refreshToken };
  return true;
}

function persist(refreshToken: string | null): void {
  try {
    if (refreshToken) localStorage.setItem(STORAGE_KEY, refreshToken);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* 隐私模式下 localStorage 会抛异常；存不下就退化成只在内存里，不影响本次使用 */
  }
}

function read(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * 清理 0.1.0 之前落在磁盘上的 token。
 *
 * 那一版存的是 access + refresh 两个键，键名也不同。CR-006 之后我们自己也落盘了，
 * 但格式变了，旧键留着没用还容易混淆，启动时顺手抹掉。
 * 等所有设备都升过一轮后可以删掉这段。
 */
export function purgeLegacyPersistedTokens(): void {
  try {
    localStorage.removeItem('construction.accessToken');
    localStorage.removeItem('construction.refreshToken');
  } catch {
    /* 同上 */
  }
}
